import { FIT_DEFAULT } from '@src/config/videoSettings';
import { LayoutTemplateRegistry } from '@src/editor/models/layout';
import { mapToElementState } from '@src/renderer/ElementState';
import {
  findLinkByRepresentationTimeRange,
  isTimeInLinkRange,
} from '@src/stores-v2/SceneLayoutIndexStore';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  type Crop,
  makeAspectCrop,
  type PercentCrop,
} from '../../react-image-multi-crop';
import {
  type AspectRatioOption,
  clampCropToFrame,
  cloneCrop,
  computeCropAspectRatio,
  computeNumericAspectRatio,
  computeResetDisabled,
  computeSlotAspectRatio,
  computeVideoTime,
  cropToCreatomate,
  DEFAULT_CROP,
  detectAspectRatioFromCrop,
  ensurePercentCrop,
  type FitOption,
  getAspectRatio,
  mergeDisplayDeltaIntoRaw,
  resolveNestedTimeOffset,
  type SlotState,
} from './utils';

import type { Scene } from '@src/editor/models/layout/Scene';
import type {
  ILayoutSegment,
  LayoutRepresentationBinding,
  LayoutTemplate,
} from '@src/editor/models/layout/types';
import type { RendererSyncService } from '@src/editor/services/RendererSyncService';
import type { UnmappedSpeakerController } from '@src/hooks/useUnmappedSpeakerApply';
import type { ElementState } from '@src/renderer/ElementState';
import type SceneLayoutSelectionManager from '@src/stores/lib/SceneLayoutSelectionManager';
import type { SceneLayoutIndexStore } from '@src/stores-v2/SceneLayoutIndexStore';

const logger = LogManager.getLogger('ReframeVideoModal');

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface UseReframeVideoModalDeps {
  /** Current playhead time */
  time: number;
  /** Default video aspect ratio (e.g., '16:9') */
  defaultVideoAspectRatio: AspectRatioOption;
  /** Current/output video aspect ratio */
  currentVideoAspectRatio: AspectRatioOption;
  /** Whether there's a current video loaded */
  hasCurrentVideo: boolean;
  /** Timeline scenes for layout lookup */
  scenes: readonly Scene[];
  /** Scene layout index store for slot resolution */
  sceneLayoutIndexStore: SceneLayoutIndexStore;
  /** Scene layout selection manager for element resolution */
  sceneLayoutSelectionManager: SceneLayoutSelectionManager;
  /** Renderer sync service for applying updates */
  rendererSyncService: RendererSyncService | null;
  /** Find element by ID in video source */
  findElementById: (id: string) => Record<string, unknown> | undefined;
  /** Check if element is original video */
  isOriginalVideoElement: (element: Record<string, unknown>) => boolean;
  /** Find original video element at current time */
  findOriginalVideoElementAtCurrentTime: () => Record<string, unknown> | null;
  /** Set active elements */
  setActiveElements: (elementId: string) => Promise<void>;
  /** Called after apply to allow the host to refresh state (e.g., push changes to renderer) */
  onAfterApply?: () => void;
  /** Close the reframe modal */
  closeModal: () => void;
  /**
   * "Apply to {Speaker}'s Unmapped Scenes" (PLAT-11992). Null or absent where
   * it can't run.
   */
  unmappedSpeaker?: UnmappedSpeakerController | null;
}

export function useReframeVideoModal(deps: UseReframeVideoModalDeps) {
  const {
    time,
    defaultVideoAspectRatio,
    currentVideoAspectRatio,
    hasCurrentVideo,
    scenes,
    sceneLayoutIndexStore,
    sceneLayoutSelectionManager,
    rendererSyncService,
    findElementById,
    isOriginalVideoElement,
    findOriginalVideoElementAtCurrentTime,
    setActiveElements,
    onAfterApply,
    closeModal,
    unmappedSpeaker,
  } = deps;

  const [videoSource, setVideoSource] = useState<string | undefined>(undefined);
  const [videoTime, setVideoTime] = useState<number>(0);
  const [isResetDisabled, setIsResetDisabled] = useState<boolean>(false);
  const [slotStateMap, setSlotStateMap] = useState<Record<string, SlotState>>(
    {},
  );
  const [slotOrder, setSlotOrder] = useState<string[]>([]);
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  /**
   * Opt in to reframing the whole video rather than the scene under the
   * playhead. Off on every open: an Apply that silently rewrote every other
   * scene's framing is the bug this is the deliberate counterpart to
   * (PLAT-11825), so it is always something the creator asked for.
   */
  const [applyToAllScenes, setApplyToAllScenesState] = useState(false);
  /**
   * Opt in to mapping the framed face to the scene's unmapped speaker and
   * reframing that speaker's scenes that fell back. Off on every open, like
   * `applyToAllScenes`; the two exclude each other.
   */
  const [applyToUnmappedSpeaker, setApplyToUnmappedSpeakerState] =
    useState(false);
  const setApplyToAllScenes = useCallback((value: boolean) => {
    setApplyToAllScenesState(value);
    if (value) setApplyToUnmappedSpeakerState(false);
  }, []);
  const setApplyToUnmappedSpeaker = useCallback((value: boolean) => {
    setApplyToUnmappedSpeakerState(value);
    if (value) setApplyToAllScenesState(false);
  }, []);

  const videoRef = useRef<HTMLVideoElement>(null);

  /**
   * The slots as they stand, for `setDefaults({ keepCrops })` to read. A ref
   * rather than a dependency: `setDefaults` re-identifying on every drag would
   * re-fire the effects its hosts hang off it.
   */
  const slotStateMapRef = useRef(slotStateMap);
  slotStateMapRef.current = slotStateMap;

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived State
  // ─────────────────────────────────────────────────────────────────────────────

  const activeSlot = activeSlotId ? slotStateMap[activeSlotId] : undefined;
  const orderedSlots = useMemo(
    () =>
      slotOrder
        .map(id => slotStateMap[id])
        .filter((slot): slot is SlotState => !!slot),
    [slotOrder, slotStateMap],
  );
  const currentFit = activeSlot?.fit ?? FIT_DEFAULT;
  const activeSlotIndex = activeSlot
    ? orderedSlots.findIndex(slot => slot.id === activeSlot.id)
    : -1;

  const displayCrops = useMemo(
    () => orderedSlots.map(slot => clampCropToFrame(slot.crop)),
    [orderedSlots],
  );

  const displayCrop =
    activeSlotIndex >= 0 ? displayCrops[activeSlotIndex] : DEFAULT_CROP;

  /**
   * The unmapped speaker this crop would map, for the checkbox. Single-slot
   * layouts only: a split layout frames two people at once.
   */
  const framedSlot = orderedSlots.length === 1 ? orderedSlots[0] : undefined;
  const unmappedSpeakerOffer = useMemo(() => {
    if (!unmappedSpeaker || !framedSlot?.sceneId) return null;
    const crop = framedSlot.crop ?? DEFAULT_CROP;
    return unmappedSpeaker.describe(framedSlot.sceneId, {
      x: crop.x / 100,
      y: crop.y / 100,
      width: crop.width / 100,
      height: crop.height / 100,
    });
  }, [unmappedSpeaker, framedSlot?.sceneId, framedSlot?.crop]);

  const canChangeAspectRatio: boolean = !(
    orderedSlots.length === 1 && orderedSlots[0]?.fit === 'cover'
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Slot Link Resolution
  // ─────────────────────────────────────────────────────────────────────────────

  const resolveSlotLinks = useCallback(
    (
      elementState: ElementState | null,
    ): Map<string, LayoutRepresentationBinding> => {
      const slotLinks = new Map<string, LayoutRepresentationBinding>();
      const activeRepresentationId = elementState?.source?.id;

      if (activeRepresentationId) {
        const representationRecord =
          sceneLayoutIndexStore.getRepresentationRoleRecordFor(
            activeRepresentationId,
            time,
          );

        if (representationRecord?.link) {
          slotLinks.set(
            representationRecord.link.representationId,
            representationRecord.link,
          );

          const parentId = representationRecord.link.parentId;
          if (parentId) {
            const parentRecords =
              sceneLayoutIndexStore.getRecords(parentId) ?? [];
            for (const record of parentRecords) {
              if (
                !record?.link?.representationId ||
                record?.link?.sceneId !== representationRecord?.link.sceneId
              ) {
                continue;
              }
              slotLinks.set(record.link.representationId, record.link);
            }
          }
        }

        // Fallback: the active id is a representation the lookup above could
        // not place — it holds records for several scenes and none of their
        // ranges covers the playhead, which is what a merged representation
        // looks like off its own scene. Reach the slots through the parent,
        // but only the ones belonging to ONE scene: the parent is the original
        // video and spans the whole timeline, so taking all of its records
        // makes every scene in the clip a slot, and Apply writes the crop to
        // every one of them — reframing one scene reframed them all
        // (PLAT-11825).
        if (slotLinks.size === 0) {
          const parentId = sceneLayoutIndexStore.resolveParentId(
            activeRepresentationId,
          );
          if (parentId) {
            const parentRecords =
              sceneLayoutIndexStore.getRecords(parentId) ?? [];
            // The scene under the playhead, or — when nothing covers it — the
            // parent's first scene, matching the branch below rather than
            // leaving the modal with no slots at all.
            const sceneId = (
              parentRecords.find(
                record =>
                  record?.link?.representationId &&
                  isTimeInLinkRange(record.link, time),
              ) ?? parentRecords.find(record => record?.link?.representationId)
            )?.link.sceneId;

            for (const record of parentRecords) {
              if (
                !record?.link?.representationId ||
                record.link.sceneId !== sceneId
              ) {
                continue;
              }
              slotLinks.set(record.link.representationId, record.link);
            }
          }
        }

        // Fallback: element is a parent (original video) — resolve its
        // representation children directly from its own records.
        // Only pick the record whose scene covers the current playhead time.
        if (slotLinks.size === 0) {
          const ownRecords =
            sceneLayoutIndexStore.getRecords(activeRepresentationId) ?? [];
          for (const record of ownRecords) {
            if (
              record.role === 'parent' &&
              record.link?.representationId &&
              isTimeInLinkRange(record.link, time)
            ) {
              slotLinks.set(record.link.representationId, record.link);
            }
          }
          // If no time-matched record found, fall back to first parent record
          if (slotLinks.size === 0) {
            const firstParent = ownRecords.find(
              r => r.role === 'parent' && r.link?.representationId,
            );
            if (firstParent) {
              slotLinks.set(
                firstParent.link.representationId,
                firstParent.link,
              );
            }
          }
        }

        // Fallback: element is a layout composition. Its records only carry the
        // 'composition' role, so none of the lookups above can match and the
        // modal would render with no slots at all (PLAT-11065). Resolve the
        // single representation visible at the playhead from the composition's
        // own records. Deliberately matches on the representation range only:
        // the scene range would match every part of the scene, and one slot per
        // template slot is what the modal expects (a composition belongs to one
        // slot — `layout_composition_<layoutId>_<slotName>`).
        if (slotLinks.size === 0) {
          const compositionLinks = (
            sceneLayoutIndexStore.getRecords(activeRepresentationId) ?? []
          )
            .filter(record => record.role === 'composition')
            .map(record => record.link)
            .filter(link => !!link?.representationId);

          const visibleLink = findLinkByRepresentationTimeRange(
            compositionLinks,
            time,
          );
          if (visibleLink) {
            slotLinks.set(visibleLink.representationId, visibleLink);
          }
        }
      }

      return slotLinks;
    },
    [sceneLayoutIndexStore, time],
  );

  /**
   * Every slot reads its crop from the bindings returned here, so the one
   * scene they are taken from has to be the scene all the links belong to.
   * Each branch of `resolveSlotLinks` keeps to a single scene for exactly that
   * reason — relax one and the slots for the other scenes open on this scene's
   * crop, which Apply then writes over their own framing (PLAT-11825).
   */
  const getLayoutTemplateAndBindings = useCallback(
    (
      slotLinks: Map<string, LayoutRepresentationBinding>,
    ): {
      template: LayoutTemplate | undefined;
      bindings: ILayoutSegment['bindings'] | undefined;
    } => {
      if (slotLinks.size === 0) {
        return { template: undefined, bindings: undefined };
      }

      const firstLink = Array.from(slotLinks.values())[0];
      if (!firstLink?.sceneId) {
        return { template: undefined, bindings: undefined };
      }

      const scene = scenes.find((s: Scene) => s.id === firstLink.sceneId);

      if (!scene) {
        return { template: undefined, bindings: undefined };
      }

      const templateRegistry = LayoutTemplateRegistry.getInstance();
      const template = templateRegistry.getTemplate(
        scene.layout.layoutTemplateId,
      );

      return { template, bindings: scene.layout.bindings };
    },
    [scenes],
  );

  const buildSlotState = useCallback(
    (
      representationId: string,
      link: LayoutRepresentationBinding,
      rawElement: Record<string, unknown>,
      resolvedSlotState: ElementState | null,
      layoutTemplate: LayoutTemplate | undefined,
      bindings: ILayoutSegment['bindings'],
      /** See `resolveNestedTimeOffset` — 0 when the element is top-level. */
      nestedTimeOffset: number,
    ): SlotState | null => {
      const slotSource = resolvedSlotState?.source ?? rawElement;

      if (slotSource?.locked) {
        return null;
      }

      const fitCandidate = slotSource?.fit ?? 'cover';
      const fitValue: FitOption =
        fitCandidate === 'contain' || fitCandidate === 'fill'
          ? fitCandidate
          : 'cover';

      const sceneSlotKeyFrame =
        bindings?.[link.slotName]?.transformKeyframes?.[0];

      // When no keyframe exists (e.g. native aspect ratio where no crop is
      // needed by default), create a full-frame crop so the user can still
      // adjust the framing.
      const slotCrop: Crop = sceneSlotKeyFrame
        ? {
            unit: '%',
            x: sceneSlotKeyFrame.crop.x * 100,
            y: sceneSlotKeyFrame.crop.y * 100,
            width: sceneSlotKeyFrame.crop.w * 100,
            height: sceneSlotKeyFrame.crop.h * 100,
          }
        : { unit: '%', x: 0, y: 0, width: 100, height: 100 };

      const videoSourceValue =
        typeof slotSource?.source === 'string'
          ? slotSource.source
          : typeof rawElement?.source === 'string'
            ? rawElement.source
            : undefined;

      let aspectRatio: number | undefined;
      if (layoutTemplate?.id === 'cover') {
        aspectRatio = getAspectRatio(currentVideoAspectRatio);
      } else if (sceneSlotKeyFrame?.cropAspectRatio) {
        aspectRatio = sceneSlotKeyFrame.cropAspectRatio;
      } else {
        const cropAspectRatio = computeCropAspectRatio(
          slotCrop,
          getAspectRatio(defaultVideoAspectRatio) || 16 / 9,
        );
        const slotAspectRatio = computeSlotAspectRatio(
          layoutTemplate,
          link?.slotName || 'main',
          currentVideoAspectRatio
            ? getAspectRatio(currentVideoAspectRatio)
            : undefined,
        );
        aspectRatio =
          layoutTemplate?.type !== 'basic' ? slotAspectRatio : cropAspectRatio;
      }

      return {
        id: representationId,
        slotName: link?.slotName || `Slot`,
        sceneId: link?.sceneId,
        elementState: resolvedSlotState ?? null,
        crop: slotCrop,
        initialCrop: cloneCrop(slotCrop),
        fit: fitValue,
        initialFit: fitValue,
        videoSource: videoSourceValue,
        videoTime: computeVideoTime(rawElement, time - nestedTimeOffset),
        aspectRatio,
        selectedAspectRatio: detectAspectRatioFromCrop(slotCrop, aspectRatio),
      };
    },
    [currentVideoAspectRatio, defaultVideoAspectRatio, time],
  );

  const buildSlotStates = useCallback(
    (
      element: Record<string, unknown>,
      resolvedElement: ElementState | null,
      slotLinks: Map<string, LayoutRepresentationBinding>,
      layoutTemplate: LayoutTemplate | undefined,
      bindings: ILayoutSegment['bindings'] | undefined,
    ): { entries: SlotState[]; order: string[] } => {
      const entries: SlotState[] = [];
      const order: string[] = [];

      if (!bindings) {
        return { entries, order };
      }

      const elementId = typeof element.id === 'string' ? element.id : undefined;
      const elementSourceId = resolvedElement?.source?.id ?? elementId;

      for (const [representationId, link] of slotLinks.entries()) {
        const rawElement =
          findElementById(representationId) ??
          (representationId === elementId ? element : undefined);

        if (!rawElement) {
          continue;
        }

        const originalMatch = isOriginalVideoElement(rawElement);
        const sourceMatches =
          typeof rawElement?.source === 'string' &&
          typeof element?.source === 'string' &&
          rawElement.source === element.source;

        if (!originalMatch && !sourceMatches) {
          continue;
        }

        const resolvedSlotState =
          representationId === elementSourceId
            ? resolvedElement
            : mapToElementState(rawElement);

        const slotState = buildSlotState(
          representationId,
          link,
          rawElement,
          resolvedSlotState,
          layoutTemplate,
          bindings,
          // The parts of a laid-out track live inside the composition, so their
          // `time` is relative to it while the playhead is absolute. `null`
          // means this element isn't in there at all — shift by nothing.
          resolveNestedTimeOffset(element, representationId) ?? 0,
        );

        if (slotState) {
          entries.push(slotState);
          order.push(representationId);
        }
      }

      return { entries, order };
    },
    [findElementById, isOriginalVideoElement, buildSlotState],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // State Reset
  // ─────────────────────────────────────────────────────────────────────────────

  const resetState = useCallback(() => {
    setSlotStateMap({});
    setSlotOrder([]);
    setActiveSlotId(null);
    setIsResetDisabled(true);
    setVideoSource(undefined);
    setVideoTime(0);
    setApplyToAllScenesState(false);
    setApplyToUnmappedSpeakerState(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // setDefaults (Slot Initialization)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Re-derive the slots for whatever is at the playhead.
   *
   * `keepCrops` carries the crop the creator is holding onto the scene being
   * moved to, matched by slot name, instead of loading that scene's own — the
   * preview behind "Apply framing to all scenes", which is the framing every
   * scene is about to get. Nothing is written: `initialCrop` still holds the
   * scene's real framing, so Reset returns to it and Apply is still what
   * commits.
   */
  const setDefaults = useCallback(
    (options?: { keepCrops?: boolean }) => {
      const previousSlots = options?.keepCrops
        ? Object.values(slotStateMapRef.current)
        : [];

      // Reset state first
      setSlotStateMap({});
      setSlotOrder([]);
      setActiveSlotId(null);
      setIsResetDisabled(true);

      const element = findOriginalVideoElementAtCurrentTime();
      if (!element) {
        setVideoSource(undefined);
        setVideoTime(0);
        return;
      }

      // Select the main video element when entering the modal
      const elementId = typeof element.id === 'string' ? element.id : undefined;
      if (elementId) {
        void setActiveElements(elementId);
      }

      // Resolve element state
      const elementState = mapToElementState(element);
      let resolvedElement = elementState;
      try {
        resolvedElement =
          sceneLayoutSelectionManager.resolveElementForActivation(elementState);
      } catch (_error) {
        resolvedElement = elementState;
      }

      // Resolve slot links
      const slotLinks = resolveSlotLinks(resolvedElement);

      // Get layout template and bindings
      const { template: layoutTemplate, bindings } =
        getLayoutTemplateAndBindings(slotLinks);

      // Build slot states
      const { entries, order } = buildSlotStates(
        element,
        resolvedElement,
        slotLinks,
        layoutTemplate,
        bindings,
      );

      if (entries.length === 0) {
        logger.error('No slots could be resolved.', {
          elementId: element.id,
          resolvedElementId: resolvedElement?.source?.id,
          activeRepresentationId: resolvedElement?.source?.id,
        });
        setVideoSource(undefined);
        setVideoTime(0);
        return;
      }

      // Finalize state
      const nextSlotStateMap = entries.reduce<Record<string, SlotState>>(
        (acc, slot) => {
          acc[slot.id] = slot;
          return acc;
        },
        {},
      );

      if (previousSlots.length > 0) {
        const carriedBySlotName = new Map(
          previousSlots.map(slot => [slot.slotName, slot]),
        );
        for (const slot of Object.values(nextSlotStateMap)) {
          const carried = carriedBySlotName.get(slot.slotName);
          // A scene laid out differently has no slot of this name to carry the
          // crop into, so it keeps — and previews — its own framing.
          if (!carried) {
            continue;
          }
          slot.crop = cloneCrop(carried.crop);
          slot.aspectRatio = carried.aspectRatio;
          slot.selectedAspectRatio = carried.selectedAspectRatio;
        }
      }

      setSlotStateMap(nextSlotStateMap);
      setSlotOrder(order);

      const initialSlotId = order[0] ?? null;
      setActiveSlotId(initialSlotId);

      const initialSlot = initialSlotId
        ? nextSlotStateMap[initialSlotId]
        : null;
      setVideoSource(initialSlot?.videoSource ?? undefined);
      setVideoTime(initialSlot?.videoTime ?? 0);

      setIsResetDisabled(computeResetDisabled(nextSlotStateMap));
    },
    [
      findOriginalVideoElementAtCurrentTime,
      setActiveElements,
      sceneLayoutSelectionManager,
      resolveSlotLinks,
      getLayoutTemplateAndBindings,
      buildSlotStates,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Crop Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const applyAspectToCrop = useCallback(
    (currentCrop: Crop, cropAspect: number | undefined): Crop => {
      if (!cropAspect) return currentCrop;

      const video = videoRef.current;
      if (!video) return currentCrop;

      const { width, height } = video.getBoundingClientRect();
      if (!width || !height) return currentCrop;

      const percentCrop: PercentCrop = {
        unit: '%',
        x: currentCrop.x,
        y: currentCrop.y,
        width: currentCrop.width,
        height: currentCrop.height,
      };

      return makeAspectCrop(percentCrop, cropAspect, width, height);
    },
    [],
  );

  const applyCropUpdate = useCallback((slotId: string, nextCrop: Crop) => {
    const normalizedCrop = ensurePercentCrop(nextCrop);
    setSlotStateMap(prev => {
      const currentSlot = prev[slotId];
      if (!currentSlot) {
        return prev;
      }

      const nextMap = {
        ...prev,
        [slotId]: {
          ...currentSlot,
          crop: cloneCrop(normalizedCrop),
        },
      };

      setIsResetDisabled(computeResetDisabled(nextMap));
      return nextMap;
    });
  }, []);

  const handleAspectRatioChange = useCallback(
    (newRatio: AspectRatioOption) => {
      if (!activeSlotId) return;

      setSlotStateMap(prev => {
        const currentSlot = prev[activeSlotId];
        if (!currentSlot) return prev;

        const newAspect = computeNumericAspectRatio(
          newRatio,
          getAspectRatio(defaultVideoAspectRatio) || 16 / 9,
        );

        const previousDisplay = clampCropToFrame(currentSlot.crop);
        const adjustedDisplay = applyAspectToCrop(previousDisplay, newAspect);
        const mergedRaw = mergeDisplayDeltaIntoRaw(
          currentSlot.crop,
          previousDisplay,
          adjustedDisplay,
        );

        return {
          ...prev,
          [activeSlotId]: {
            ...currentSlot,
            selectedAspectRatio: newRatio,
            aspectRatio: newAspect,
            crop: mergedRaw,
          },
        };
      });
    },
    [activeSlotId, applyAspectToCrop, defaultVideoAspectRatio],
  );

  const handleCropChange = useCallback(() => {
    // Multi-crop updates handled via onChangeCrops callback.
  }, []);

  const handleMultiCropChange = useCallback(
    (
      _pixelCrops: Crop[],
      percentCrops: PercentCrop[],
      changedIndex: number,
    ) => {
      // If changedIndex is -1, update all crops
      if (changedIndex === -1) {
        percentCrops.forEach((nextDisplayCrop, index) => {
          const slot = orderedSlots[index];
          if (!slot || !nextDisplayCrop) {
            return;
          }
          const previousDisplay = clampCropToFrame(slot.crop);
          const mergedRaw = mergeDisplayDeltaIntoRaw(
            slot.crop,
            previousDisplay,
            ensurePercentCrop(nextDisplayCrop),
          );
          applyCropUpdate(slot.id, mergedRaw);
        });
        return;
      }

      const slot = orderedSlots[changedIndex];
      const nextDisplayCrop = percentCrops[changedIndex];

      if (!slot || !nextDisplayCrop) {
        return;
      }

      const previousDisplay = clampCropToFrame(slot.crop);
      const mergedRaw = mergeDisplayDeltaIntoRaw(
        slot.crop,
        previousDisplay,
        ensurePercentCrop(nextDisplayCrop),
      );

      applyCropUpdate(slot.id, mergedRaw);
    },
    [applyCropUpdate, orderedSlots],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Modal Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    closeModal();
    resetState();
  }, [closeModal, resetState]);

  const handleSlotSelect = useCallback(
    (slotId: string) => {
      if (slotId === activeSlotId) {
        return;
      }
      setActiveSlotId(slotId);
    },
    [activeSlotId],
  );

  /**
   * `keepOpen` applies the crops without closing — for a caller that has
   * somewhere to go next inside the modal (moving to another scene), rather
   * than the Apply button, which is the end of the session.
   *
   * Returns whether the crops reached the sync service. A `keepOpen` caller
   * must not carry on to whatever it had planned when this is `false` — the
   * crops are still only in slot state, and moving on discards them.
   */
  const handleApply = useCallback(
    async (options?: { keepOpen?: boolean }): Promise<boolean> => {
      const keepOpen = options?.keepOpen === true;
      if (!hasCurrentVideo || orderedSlots.length === 0) {
        logger.warn('Apply skipped: no video at the playhead, or no slots.', {
          hasCurrentVideo,
          slotCount: orderedSlots.length,
        });
        return false;
      }

      if (!rendererSyncService) {
        logger.warn('Apply skipped: no renderer sync service.');
        return false;
      }

      type CreatomateCrop = ReturnType<typeof cropToCreatomate>;

      const pendingRepresentationUpdates: Array<{
        slot: SlotState;
        element: ElementState;
        elementId: string;
        cropValues: CreatomateCrop;
      }> = [];

      for (const slot of orderedSlots) {
        const slotCrop = slot.crop ?? DEFAULT_CROP;
        const cropValues = cropToCreatomate(slotCrop);
        const elementId = slot.elementState?.source?.id ?? slot.id;

        if (!elementId) {
          continue;
        }

        // Always attempt the update — for native aspect ratios (e.g. 16:9 source
        // in 16:9 output), elementState may be null because the layout engine
        // doesn't create separate representation elements with crop data. The
        // sync service can still create scene bindings from the element ID alone.
        {
          // Recalculate aspect ratio if user is in Custom mode
          const cropAspectRatio =
            slot.selectedAspectRatio === 'custom'
              ? computeCropAspectRatio(
                  slotCrop,
                  getAspectRatio(defaultVideoAspectRatio) || 16 / 9,
                )
              : slot.aspectRatio;

          // Use existing element state, or build a minimal one when no
          // representation element exists in the video source. Only
          // source.id and the crop properties (x, y, width, height,
          // cropAspectRatio) are read by RendererSyncService.buildUpdatedBinding;
          // the remaining fields are structural padding for the ElementState type.
          const baseElement: ElementState =
            slot.elementState ??
            ({
              source: { id: elementId },
              track: 0,
              localTime: 0,
              globalTime: 0,
              duration: 0,
              exitDuration: 0,
              trimStart: 0,
              elements: [],
            } as ElementState);

          const nextElement: ElementState = {
            ...baseElement,
            source: {
              ...baseElement.source,
              x: `${cropValues.x}%`,
              y: `${cropValues.y}%`,
              width: `${cropValues.width}%`,
              height: `${cropValues.height}%`,
              cropAspectRatio,
            },
          };

          pendingRepresentationUpdates.push({
            slot,
            element: nextElement,
            elementId,
            cropValues,
          });
        }
      }

      if (pendingRepresentationUpdates.length === 0) {
        logger.warn('No valid slots to update', {
          orderedSlots,
        });
        if (!keepOpen) {
          handleClose();
        }
        return false;
      }

      // Opted in to the unmapped speaker: save the framed face as theirs, and
      // write this scene's crop plus their fallen-back scenes in one command,
      // so one undo reverts the framing.
      if (
        applyToUnmappedSpeaker &&
        unmappedSpeakerOffer?.isNamed &&
        unmappedSpeaker
      ) {
        const [{ slot, element }] = pendingRepresentationUpdates;
        const planned = slot.sceneId
          ? await unmappedSpeaker.plan(slot.sceneId, unmappedSpeakerOffer)
          : null;
        if (planned?.ok) {
          const updatedSceneIds = rendererSyncService.commitSceneUpdates([
            ...rendererSyncService.buildManualSlotSceneUpdates(
              [{ slotName: slot.slotName, element }],
              [slot.sceneId!],
            ),
            ...planned.value.sceneUpdates,
          ]);
          unmappedSpeaker.commitMap(planned.value.speakerMap);
          logger.info("Mapped the face and framed the speaker's scenes.", {
            speakerLabel: unmappedSpeakerOffer.speakerLabel,
            updatedScenes: updatedSceneIds.size,
          });
          onAfterApply?.();
          if (!keepOpen) {
            handleClose();
          }
          return true;
        }
        // The mapping didn't save: the creator still gets the framing they
        // drew, on this scene.
        logger.warn(
          'Unmapped-speaker apply skipped; applying this scene only.',
          {
            error: planned && !planned.ok ? planned.error : 'no scene',
          },
        );
      }

      // Opted in, so write the same framing to every scene with a slot of this
      // name — the one being framed included, in the same command, so one undo
      // reverts the lot. A scene already framed this way is skipped rather
      // than failing the apply, which is how the current scene's framing,
      // left as it is, still reaches the others.
      if (applyToAllScenes) {
        // No binding on the scene being framed means the crops never had
        // anywhere to land — spreading them would hand the other scenes a
        // framing this one never got.
        const framedSceneHasBindings = pendingRepresentationUpdates.every(
          ({ slot }) =>
            !!scenes.find(scene => scene.id === slot.sceneId)?.layout
              .bindings?.[slot.slotName],
        );
        if (!framedSceneHasBindings) {
          logger.warn('Apply to all skipped: framed scene has no binding.', {
            orderedSlots,
          });
          if (!keepOpen) {
            handleClose();
          }
          return false;
        }

        const framedSceneIds = new Set(
          pendingRepresentationUpdates.map(update => update.slot.sceneId),
        );
        const updatedSceneIds =
          rendererSyncService.applyManualSlotUpdatesToScenes(
            pendingRepresentationUpdates.map(update => ({
              slotName: update.slot.slotName,
              element: update.element,
            })),
            scenes.map(scene => scene.id),
          );

        logger.info('Applied framing to all scenes.', {
          framedScenes: framedSceneIds.size,
          candidateScenes: scenes.length,
          updatedScenes: updatedSceneIds.size,
        });

        onAfterApply?.();
        if (!keepOpen) {
          handleClose();
        }
        return true;
      }

      const appliedElementIds =
        rendererSyncService.applyManualRepresentationUpdates(
          pendingRepresentationUpdates.map(update => update.element),
        );

      // Nothing reached a scene binding, so the crops are still only in slot
      // state — same position as having had no slots to update at all. A
      // `keepOpen` caller has to hear about that: told the apply succeeded, it
      // moves on and discards the framing it was asked to keep.
      if (appliedElementIds.size === 0) {
        logger.warn('No representation updates were applied via layout sync.', {
          orderedSlots,
          pendingRepresentationUpdates,
        });
        if (!keepOpen) {
          handleClose();
        }
        return false;
      }

      // Allow host to refresh state (e.g., push scene changes to renderer)
      onAfterApply?.();

      if (!keepOpen) {
        handleClose();
      }

      return true;
    },
    [
      hasCurrentVideo,
      rendererSyncService,
      orderedSlots,
      defaultVideoAspectRatio,
      applyToAllScenes,
      applyToUnmappedSpeaker,
      unmappedSpeakerOffer,
      unmappedSpeaker,
      scenes,
      onAfterApply,
      handleClose,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Sync Effects (to be used with useEffect in component)
  // ─────────────────────────────────────────────────────────────────────────────

  const syncVideoSourceAndTime = useCallback(() => {
    if (!activeSlot) {
      return;
    }

    if (activeSlot.videoSource !== videoSource) {
      setVideoSource(activeSlot.videoSource);
    }

    if (activeSlot.videoTime !== videoTime) {
      setVideoTime(activeSlot.videoTime);
    }
  }, [activeSlot, videoSource, videoTime]);

  return {
    // State
    videoSource,
    videoTime,
    isResetDisabled,
    slotStateMap,
    slotOrder,
    activeSlotId,
    applyToAllScenes,
    applyToUnmappedSpeaker,
    unmappedSpeakerOffer,
    videoRef,

    // Derived state
    activeSlot,
    orderedSlots,
    currentFit,
    activeSlotIndex,
    displayCrops,
    displayCrop,
    canChangeAspectRatio,
    defaultVideoAspectRatio,

    // Handlers
    setDefaults,
    handleAspectRatioChange,
    handleCropChange,
    handleMultiCropChange,
    handleClose,
    handleSlotSelect,
    handleApply,
    setApplyToAllScenes,
    setApplyToUnmappedSpeaker,

    // Sync
    syncVideoSourceAndTime,
  };
}
